import React from "react"

import { Grid, Typography, Button } from "@material-ui/core"
import Breadcrumb from "@/components/BreadcrumbNew"
import PortletWithNewsContent from "@/components/PortletWithNewsContent"
import DataTable, { TableColumn } from "@/components/DataTable"
import { formsLinks } from "@/utils/link"
import HumanHandshakeSVGIcon from "@/assets/images/illustrations/Partnership_human_handshake.svg"
import WhatsAppSVGIcon from "@/assets/images/logos/whatsapp_logo.svg"
import { DiamondIcon } from "@/assets/icons"

import {
	Link as LinkIcon,
	People as PeopleIcon,
	DoubleArrow as DoubleArrowIcon,
	CheckCircle as CheckCircleIcon,
	Cancel as CancelIcon,
	VerifiedUser as VerifiedUserIcon,
	EmojiEvents as TrophyIcon,
	ChatBubbleOutline as ChatIcon
} from "@material-ui/icons"

import useStyles from "@/pages/Admin/Partnerships/styles"

type PartnershipStage = {
	icon: React.ReactNode
	title: string
	description: string
}

type PartnershipLevel = {
	name: string
	icon: React.ReactNode
	color: string
}

type PartnershipBenefitLevel = boolean | string

type PartnershipBenefit = {
	name: string
	description: string
	silverLevel: PartnershipBenefitLevel
	goldLevel: PartnershipBenefitLevel
	diamondLevel: PartnershipBenefitLevel
}

type ParticipationReason = {
	title: string
}

const Partnerships: React.FC = () => {
	const classes = useStyles()

	const PARTNERSHIP_STAGES: PartnershipStage[] = [
		{
			icon: <LinkIcon className={classes.stageIconSize} />,
			title: "Torne-se parceiro",
			description: "Receba um link exclusivo de indicações."
		},
		{
			icon: <PeopleIcon className={classes.stageIconSize} />,
			title: "Indique clientes",
			description: "Acompanhe tudo pelo seu painel."
		},
		{
			icon: <DoubleArrowIcon className={classes.stageIconRotated} />,
			title: "Ganhe recompensas",
			description: "Cada conta ativa te aproxima do próximo estágio e desbloqueia um novo conjunto de benefícios."
		}
	]

	const PARTNERSHIP_LEVELS: PartnershipLevel[] = [
		{
			name: "Prata",
			icon: <VerifiedUserIcon />,
			color: classes.silverLevelColor
		},
		{
			name: "Ouro",
			icon: <TrophyIcon />,
			color: classes.goldLevelColor
		},
		{
			name: "Diamond",
			icon: <DiamondIcon />,
			color: classes.diamondLevelColor
		}
	]

	const PARTICIPATION_REASONS: ParticipationReason[] = [
		{ title: "Mais receita" },
		{ title: "Mais autoridade" },
		{ title: "Mais oportunidades" },
		{ title: "Mais Letalk ao seu lado" }
	]

	const PARTNERSHIP_BENEFITS: PartnershipBenefit[] = [
		{
			name: "Ações de Co-Marketing",
			description: "Una-se à Letalk em ações e campanhas exclusivas.",
			silverLevel: true,
			goldLevel: true,
			diamondLevel: true
		},
		{
			name: "Conta gratuita na Letalk",
			description: "Use internamente e profissionalize sua operação.",
			silverLevel: true,
			goldLevel: true,
			diamondLevel: true
		},
		{
			name: "Benefícios para seus clientes",
			description: "Presenteie quem você indica por 3 meses.",
			silverLevel: true,
			goldLevel: true,
			diamondLevel: true
		},
		{
			name: "Acesso antecipado a novidades",
			description: "Veja antes. Use antes.",
			silverLevel: true,
			goldLevel: true,
			diamondLevel: true
		},
		{
			name: "Usuário extra",
			description: "Mais controle e mais escala para administrar clientes.",
			silverLevel: false,
			goldLevel: true,
			diamondLevel: true
		},
		{
			name: "Prioridade estratégica",
			description: "Porque parceria de verdade tem atenção especial.",
			silverLevel: false,
			goldLevel: true,
			diamondLevel: true
		},
		{
			name: "Surpresas exclusivas",
			description: "Algumas coisas... só dentro do programa. (Sim, deixamos no mistério de propósito.)",
			silverLevel: false,
			goldLevel: false,
			diamondLevel: true
		},
		{
			name: "Comissionamento",
			description: "Receba um percentual de comissionamento e setup.",
			silverLevel: "10% de comissionamento recorrente e 25% de setup",
			goldLevel: "20% de comissionamento recorrente e 25% de setup",
			diamondLevel: "20% de comissionamento recorrente e 50% de setup"
		}
	]

	type TableRow = {
		benefit: PartnershipBenefit
		silverLevel: PartnershipBenefitLevel
		goldLevel: PartnershipBenefitLevel
		diamondLevel: PartnershipBenefitLevel
	}

	const tableData: TableRow[] = PARTNERSHIP_BENEFITS.map(benefit => ({
		benefit,
		silverLevel: benefit.silverLevel,
		goldLevel: benefit.goldLevel,
		diamondLevel: benefit.diamondLevel
	}))

	return (
		<Grid
			container
			direction="column"
			spacing={6}
		>
			<Grid item>
				<Grid container direction="column" spacing={3}>
					<Grid item>
						<Breadcrumb
							data={[
								{ name: "Parcerias", pathname: "/admin/partnerships" }
							]}
						/>
					</Grid>

					<Grid item className={classes.portlet}>
						<PortletWithNewsContent
							body={{}}
							title={{
								value: "Quero ser parceiro"
							}}
						>
							<Grid
								container
								justifyContent="center"
								direction="column"
							>
								<Typography
									variant="h1"
									className={classes.bodyText}
								>
									Descubra o que está esperando por você.
								</Typography>

								<Button
									className={classes.button}
									variant="contained"
									href={formsLinks.partnerships}
									target="_blank"
								>
									Começar agora
								</Button>
							</Grid>
						</PortletWithNewsContent>
					</Grid>
					<Grid item>
						<Grid container direction="row">
							<Grid item xs={6}>
								<Grid container>
									<Grid item>
										<Typography
											className={classes.calloutTitle}
										>
											Torne-se parceiro Letalk e aumente seus ganhos
										</Typography>
									</Grid>
									<Grid item>
										<Typography
										>
											A Letalk se torna sua aliada no processo comercial, ajudando você a gerar confiança, previsibilidade e resultados rápidos para cada cliente indicado. <br />
											Enquanto seus clientes ganham eficiência, você ganha receita, autoridade e vantagens que evoluem conforme cresce dentro do programa.
										</Typography>
									</Grid>
								</Grid>
							</Grid>
							<Grid item xs={6}>
								<img
									src={HumanHandshakeSVGIcon}
									className={classes.calloutImage}
								/>
							</Grid>
						</Grid>
					</Grid>
					<Grid item>
						<Grid container direction="column" spacing={4}>
							<Grid item>
								<Typography variant="h1" className={classes.sectionTitle}>
									Como funciona
								</Typography>
							</Grid>

							<Grid item>
								<Grid container spacing={3} justifyContent="center" alignItems="stretch">
									{
										PARTNERSHIP_STAGES.map((stage, index) => (
											<Grid item xs={12} sm={4} key={index}>
												<Grid
													container
													direction="column"
													spacing={1}
													className={classes.stageContainer}
												>
													<Grid item>
														{stage.icon}
													</Grid>
													<Grid item>
														<Typography variant="h2" className={classes.stageTitle}>
															{stage.title}
														</Typography>
													</Grid>
													<Grid item>
														<Typography variant="body1" className={classes.stageDescription}>
															{stage.description}
														</Typography>
													</Grid>
												</Grid>
											</Grid>
										))
									}
								</Grid>
							</Grid>
						</Grid>
					</Grid>
					<Grid item>
						<Grid container direction="column" spacing={4}>
							<Grid item>
								<Typography variant="h1" className={classes.sectionTitle}>
									Níveis
								</Typography>
							</Grid>

							<Grid item>
								<Grid className={classes.tableContainer}>
									<DataTable<TableRow>
										columns={[
											{
												field: "benefit",
												label: "Benefícios",
												headRender: () => (
													<Typography variant="h6" className={classes.tableBenefitsColumnHeader}>
														Benefícios
													</Typography>
												),
												rowRender: (benefit) => {
													const benefitObj = benefit as PartnershipBenefit
													return (
														<Grid container direction="column">
															<Typography variant="body1" className={classes.tableBenefitName}>
																{benefitObj.name}
															</Typography>
															{benefitObj.description && (
																<Typography variant="body2" className={classes.tableBenefitDescription}>
																	{benefitObj.description}
																</Typography>
															)}
														</Grid>
													)
												}
											},
											...PARTNERSHIP_LEVELS.map((level, levelIndex) => {
												const levelFields: (keyof TableRow)[] = ["silverLevel", "goldLevel", "diamondLevel"]
												const fieldName = levelFields[levelIndex]

												return {
													field: fieldName,
													label: level.name,
													align: "center" as const,
													headRender: () => (
														<Grid container direction="column" alignItems="center">
															<Grid item className={`${classes.levelIcon} ${level.color}`}>
																{level.icon}
															</Grid>
															<Grid item>
																<Typography variant="h6" className={`${classes.levelName} ${level.color}`}>
																	{level.name}
																</Typography>
															</Grid>
														</Grid>
													),
													rowRender: (levelValue: PartnershipBenefitLevel) => (
														typeof levelValue === "boolean" ? (
															levelValue ? (
																<CheckCircleIcon className={classes.tableBenefitAvailableIcon} />
															) : (
																<CancelIcon className={classes.tableBenefitUnavailableIcon} />
															)
														) : (
															<Typography variant="body2" className={classes.tableBenefitCustomText}>
																{levelValue}
															</Typography>
														)
													)
												} as TableColumn<TableRow>
											})
										]}
										data={tableData}
									/>
								</Grid>
							</Grid>
						</Grid>
					</Grid>
					<Grid item className={classes.portlet}>
						<PortletWithNewsContent
							body={{}}
							title={{
								value: "Cresça e ganhe"
							}}
						>
							<Grid
								container
								justifyContent="center"
								direction="column"
							>
								<Typography
									variant="h1"
									className={classes.bodySubtext}
								>
									Quanto mais contas ativas, maiores as vantagens e liberações no seu painel. <br />
									Você acompanha tudo de forma transparente e em tempo real.
								</Typography>
							</Grid>
						</PortletWithNewsContent>
					</Grid>
					<Grid item>
						<Grid container direction="column" spacing={4}>
							<Grid item>
								<Typography variant="h1" className={classes.sectionTitle}>
									Porque participar?
								</Typography>
							</Grid>
							<Grid item>
								<Grid container spacing={2} justifyContent="center" alignItems="stretch">
									{PARTICIPATION_REASONS.map((reason, index) => (
										<Grid item xs={12} sm={6} md={3} key={index}>
											<Grid container className={classes.participationReasonCard}>
												<Typography variant="h3" className={classes.participationReasonText}>
													{reason.title}
												</Typography>
											</Grid>
										</Grid>
									))}
								</Grid>
							</Grid>
							<Grid item>
								<Grid container justifyContent="center">
									<Grid item>
										<Typography variant="subtitle1" className={classes.sectionSubtitle}>
											Ser parceiro não é só indicar.
										</Typography>
									</Grid>

									<Grid item>
										<Typography variant="body1" style={{ textAlign: "center" }}>
											É fazer parte do ecossistema que mais cresce no WhatsApp no Brasil, com benefícios que evoluem com você.
										</Typography>
									</Grid>
								</Grid>
							</Grid>
						</Grid>
					</Grid>
					<Grid item>
						<Grid container justifyContent="center" className={classes.channelSectionOuter}>
							<Grid item>
								<Grid container direction="column" alignItems="center" className={classes.channelSectionInner}>
									<Grid item>
										<img src={WhatsAppSVGIcon} className={classes.whatsappLogo}/>
									</Grid>
									<Grid item>
										<Typography variant="h1" className={classes.channelTitle}>
											Canal exclusivo do programa
										</Typography>
									</Grid>
									<Grid item>
										<Button
											className={classes.channelButton}
											variant="contained"
											href={formsLinks.partnerships}
											target="_blank"
											startIcon={<ChatIcon />}
										>
											Fale conosco
										</Button>
									</Grid>
								</Grid>
							</Grid>
						</Grid>
					</Grid>
				</Grid>
			</Grid>
		</Grid>
	)
}

export default Partnerships
